import { axiosClientV2 } from './axios.client.js';
import { getApiV2BaseUrl } from '../config/brand.js';

function apiError(err) {
  return err?.error || err?.message || 'Request failed';
}

export async function getExtensionsCatalog() {
  try {
    const data = await axiosClientV2.get('/extensions/catalog');
    return { catalog: data, error: null };
  } catch (err) {
    return { catalog: null, error: apiError(err) };
  }
}

export async function getTenantExtensions() {
  try {
    const data = await axiosClientV2.get('/extensions/tenant');
    return { ...data, error: null };
  } catch (err) {
    return { success: false, error: apiError(err) };
  }
}

export async function updateTenantExtensions(enabledExtensions) {
  try {
    const data = await axiosClientV2.put('/extensions/tenant', { enabledExtensions });
    return { ...data, error: null };
  } catch (err) {
    return { success: false, error: apiError(err) };
  }
}

export async function listExtensionTools() {
  try {
    const data = await axiosClientV2.get('/extensions/tools');
    return { tools: data?.tools || [], error: null };
  } catch (err) {
    return { tools: [], error: apiError(err) };
  }
}

export async function listExtensionSkills() {
  try {
    const data = await axiosClientV2.get('/extensions/skills');
    return { skills: data?.skills || [], error: null };
  } catch (err) {
    return { skills: [], error: apiError(err) };
  }
}

/** Resolved skill (inline prompt) for Studio customize / view. */
export async function getResolvedSkill(skillId) {
  try {
    const data = await axiosClientV2.get(`/extensions/skills/${encodeURIComponent(skillId)}`);
    return { skill: data?.skill || null, error: null };
  } catch (err) {
    return { skill: null, error: apiError(err) };
  }
}

/** App panels drawn by enabled plugins: [{ ref, extensionId, extensionName, name, description, outputKey, height, html }]. */
export async function listAppPanels() {
  try {
    const data = await axiosClientV2.get('/extensions/app-panels');
    return { panels: data?.panels || [], error: null };
  } catch (err) {
    return { panels: [], error: apiError(err) };
  }
}

/** Sends one message to the skill behind an app's skill chat panel; resolves to { answer, toolCalls, error }. */
export async function postAppSkillChat(appId, { panelId, message, history }) {
  try {
    const data = await axiosClientV2.post(
      `/apps/${encodeURIComponent(appId)}/skill-chat`,
      { panelId, message, history },
      { timeout: 250000 },
    );
    return { answer: data?.answer || '', toolCalls: data?.toolCalls || [], error: data?.error || null };
  } catch (err) {
    return { answer: '', toolCalls: [], error: apiError(err) };
  }
}

/** Skills written in the Studio for this workspace (stored as the workspace.skills plugin). */
export async function listWorkspaceSkills() {
  try {
    const data = await axiosClientV2.get('/extensions/workspace-skills');
    return { skills: data?.skills || [], error: null };
  } catch (err) {
    return { skills: [], error: apiError(err) };
  }
}

export async function saveWorkspaceSkill(skill) {
  try {
    const data = await axiosClientV2.post('/extensions/workspace-skills', skill);
    return { skill: data?.skill || null, error: null };
  } catch (err) {
    return { skill: null, error: apiError(err) };
  }
}

export async function deleteWorkspaceSkill(skillId) {
  try {
    await axiosClientV2.delete(`/extensions/workspace-skills/${encodeURIComponent(skillId)}`);
    return { error: null };
  } catch (err) {
    return { error: apiError(err) };
  }
}

/** Runs an unsaved skill once; resolves to { ok, answer, toolCalls, error, durationMs }. */
export async function tryWorkspaceSkill(skill, message) {
  try {
    const data = await axiosClientV2.post('/extensions/workspace-skills/try', { skill, message }, { timeout: 250000 });
    return { ...data, error: data?.error || null };
  } catch (err) {
    return {
      ok: false, answer: '', toolCalls: [], error: apiError(err),
    };
  }
}

export async function uploadSideloadManifest(manifest) {
  try {
    const data = await axiosClientV2.post('/extensions/sideload', { manifest });
    return { ...data, error: null };
  } catch (err) {
    return { success: false, error: apiError(err) };
  }
}

export async function getSideloadManifest(extensionId) {
  try {
    const data = await axiosClientV2.get(`/extensions/sideload/${encodeURIComponent(extensionId)}`);
    return { manifest: data?.manifest, error: null };
  } catch (err) {
    return { manifest: null, error: apiError(err) };
  }
}

export async function deleteSideloadManifest(extensionId) {
  try {
    const data = await axiosClientV2.delete(`/extensions/sideload/${encodeURIComponent(extensionId)}`);
    return { ...data, error: null };
  } catch (err) {
    return { success: false, error: apiError(err) };
  }
}

export async function getExtensionAuditLog(limit = 50) {
  try {
    const data = await axiosClientV2.get('/extensions/audit', { params: { limit } });
    return { entries: data?.entries || [], error: null };
  } catch (err) {
    return { entries: [], error: apiError(err) };
  }
}

export async function getExtensionsMarketplace() {
  try {
    const data = await axiosClientV2.get('/extensions/marketplace');
    return { listings: data?.listings || [], error: null };
  } catch (err) {
    return { listings: [], error: apiError(err) };
  }
}

export async function getMarketplaceExtensionManifest(extensionId) {
  try {
    const data = await axiosClientV2.get(`/extensions/marketplace/${encodeURIComponent(extensionId)}/manifest`);
    return { manifest: data?.manifest, error: null };
  } catch (err) {
    return { manifest: null, error: apiError(err) };
  }
}

export async function getExtensionUsage() {
  try {
    const data = await axiosClientV2.get('/extensions/usage');
    return { usage: data?.usage || {}, error: null };
  } catch (err) {
    return { usage: {}, error: apiError(err) };
  }
}

/** Streaming AI chat for CLAW plugin builder (Qwen / Ollama by default). */
export async function sendExtensionAiChatStream({
  messages,
  model,
  manifest,
  clientValidationErrors,
  signal,
  onEvent,
}) {
  try {
    const body = { messages, model };
    if (manifest && typeof manifest === 'object') body.manifest = manifest;
    if (Array.isArray(clientValidationErrors) && clientValidationErrors.length) {
      body.clientValidationErrors = clientValidationErrors;
    }

    const token = localStorage.getItem('tkn');
    const base = String(getApiV2BaseUrl() || '/api/v2').replace(/\/$/, '');
    const url = `${base}/extensions/ai-chat/stream`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok || !res.body) {
      return {
        reply: '',
        proposedManifest: null,
        proposedValidation: null,
        model: null,
        error: `Stream failed (${res.status})`,
      };
    }

    const decoder = new TextDecoder();
    const reader = res.body.getReader();
    let buffer = '';
    let final = {
      reply: '',
      proposedManifest: null,
      proposedValidation: null,
      model: null,
      error: null,
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() || '';
      for (const raw of chunks) {
        const lines = raw.split('\n').map((l) => l.trim()).filter(Boolean);
        const dataLine = lines.find((l) => l.startsWith('data:'));
        if (!dataLine) continue;
        let payload;
        try {
          payload = JSON.parse(dataLine.replace(/^data:\s*/, ''));
        } catch (_) {
          continue;
        }
        onEvent?.(payload);
        if (payload?.type === 'done') {
          final = {
            reply: payload.reply ?? '',
            proposedManifest: payload.proposedManifest ?? null,
            proposedValidation: payload.proposedValidation ?? null,
            model: payload.model ?? null,
            fallback: payload.fallback ?? null,
            error: payload.error || null,
          };
        }
        if (payload?.type === 'error') {
          final.error = payload.error || 'AI stream failed';
        }
      }
    }
    return final;
  } catch (e) {
    return {
      reply: '',
      proposedManifest: null,
      proposedValidation: null,
      model: null,
      error: typeof e === 'string' ? e : (e?.message || 'AI stream failed'),
    };
  }
}
